import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

const ASYNC_CAPABLE_TIERS = new Set(['pro', 'power', 'admin']);

const normalizeTier = (tier) => {
  if (!tier) {
    return null;
  }

  return String(tier).toLowerCase();
};

const formatTierLabel = (tier) => {
  if (!tier) {
    return 'current';
  }

  const normalized = normalizeTier(tier);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const isAsyncCapablePlan = (tier) => ASYNC_CAPABLE_TIERS.has(normalizeTier(tier));

export const getCustomEventMetadata = (customEvent, options = {}) => {
  const currentPlanTier = options.currentPlanTier || null;
  const executionTier = normalizeTier(customEvent?.executionTier);
  const complexityScore = typeof customEvent?.complexityScore === 'number'
    ? customEvent.complexityScore
    : null;
  const hasSavedMetadata = Boolean(executionTier) || complexityScore !== null;

  if (!hasSavedMetadata) {
    return {
      hasSavedMetadata: false,
      executionChip: { label: 'Legacy event', color: 'default', variant: 'outlined' },
      scoreChip: { label: 'Score unavailable', color: 'default', variant: 'outlined' },
      compatibilityChip: { label: 'Compatibility unknown', color: 'default', variant: 'outlined' },
      helperText: 'Saved before execution metadata was introduced.',
    };
  }

  if (executionTier === 'fast') {
    return {
      hasSavedMetadata: true,
      executionChip: { label: 'Fast tier', color: 'success', variant: 'filled' },
      scoreChip: {
        label: complexityScore !== null ? `Score ${complexityScore}` : 'Score unavailable',
        color: complexityScore !== null ? 'info' : 'default',
        variant: 'outlined'
      },
      compatibilityChip: { label: 'Plan compatible', color: 'success', variant: 'outlined' },
      helperText: 'Eligible for fast shared evaluation on the current plan.',
    };
  }

  if (executionTier === 'async') {
    if (currentPlanTier && isAsyncCapablePlan(currentPlanTier)) {
      return {
        hasSavedMetadata: true,
        executionChip: { label: 'Async tier', color: 'warning', variant: 'filled' },
        scoreChip: {
          label: complexityScore !== null ? `Score ${complexityScore}` : 'Score unavailable',
          color: complexityScore !== null ? 'info' : 'default',
          variant: 'outlined'
        },
        compatibilityChip: { label: 'Plan compatible', color: 'success', variant: 'outlined' },
        helperText: `Runs on the slower async path and is compatible with the ${formatTierLabel(currentPlanTier)} plan.`,
      };
    }

    return {
      hasSavedMetadata: true,
      executionChip: { label: 'Async tier', color: 'warning', variant: 'filled' },
      scoreChip: {
        label: complexityScore !== null ? `Score ${complexityScore}` : 'Score unavailable',
        color: complexityScore !== null ? 'info' : 'default',
        variant: 'outlined'
      },
      compatibilityChip: {
        label: currentPlanTier ? 'Upgrade required' : 'Async-capable plan',
        color: currentPlanTier ? 'error' : 'warning',
        variant: 'outlined'
      },
      helperText: currentPlanTier
        ? `This event requires an async-capable plan. The ${formatTierLabel(currentPlanTier)} plan is not sufficient.`
        : 'This event requires an async-capable plan to remain compatible.',
    };
  }

  return {
    hasSavedMetadata: true,
    executionChip: { label: 'Tier unknown', color: 'default', variant: 'outlined' },
    scoreChip: {
      label: complexityScore !== null ? `Score ${complexityScore}` : 'Score unavailable',
      color: complexityScore !== null ? 'info' : 'default',
      variant: 'outlined'
    },
    compatibilityChip: { label: 'Compatibility unknown', color: 'default', variant: 'outlined' },
    helperText: 'Saved execution metadata is incomplete.',
  };
};

export const getCustomEventEvaluationNotice = (customEvent, options = {}) => {
  const currentPlanTier = options.currentPlanTier || null;
  const executionTier = normalizeTier(customEvent?.executionTier);

  if (!executionTier) {
    return {
      severity: 'info',
      title: 'Execution tier not yet recorded',
      message: 'This event was saved before execution metadata was introduced, so signal freshness is unknown until it is saved or compiled again.',
      detail: 'Use the saved event metadata as the source of truth once tier metadata is available.',
    };
  }

  if (executionTier === 'async') {
    return {
      severity: 'warning',
      title: 'Async evaluation path',
      message: 'This event runs on the slower async path, so new symbol signals may appear later than fast-tier events after market data changes.',
      detail: currentPlanTier && isAsyncCapablePlan(currentPlanTier)
        ? `The ${formatTierLabel(currentPlanTier)} plan supports this event, but lower-latency expectations should still be reserved for fast-tier events.`
        : 'Per-symbol activation still works, but freshness-sensitive workflows should prefer fast-tier events when possible.',
    };
  }

  if (executionTier === 'fast') {
    return null;
  }

  return {
    severity: 'info',
    title: 'Execution tier unknown',
    message: 'This event has partial execution metadata, so evaluation freshness may not be fully predictable yet.',
    detail: 'Review the saved event metadata and re-save the event if you need a confirmed fast or async tier.',
  };
};

export const CustomEventMetadataChips = ({
  customEvent,
  currentPlanTier,
  size = 'small',
  showHelperText = false,
  sx,
}) => {
  const metadata = getCustomEventMetadata(customEvent, { currentPlanTier });

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip size={size} {...metadata.executionChip} />
        <Chip size={size} {...metadata.scoreChip} />
        <Chip size={size} {...metadata.compatibilityChip} />
      </Box>
      {showHelperText && metadata.helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {metadata.helperText}
        </Typography>
      )}
    </Box>
  );
};
